import { OptionTypeBase, SelectProps } from '@cad-arc';
import { debounce, keyBy } from 'lodash';
import React, { useState } from 'react';
import { omitRef } from '../helpers/omit-ref';
import { SelectOptions } from '../option';
import { BaseSelect } from './base-select';
import type { OnInputChangeParams } from './types';

const DEFAULT_DEBOUNCE_TIMER = 250;

type Actions =
    | { type: 'SET_OPTIONS'; options: SelectOptions }
    | { type: 'SET_LOADING' }
    | { type: 'SET_ERROR'; error: string };

type State =
    | {
          options: SelectOptions;
          loading: false;
          error: undefined;
      }
    | {
          options: SelectOptions;
          loading: true;
          error: undefined;
      }
    | {
          options: SelectOptions;
          loading: false;
          error: string;
      };

type OnChangeHandler = (value: SelectOptions) => void;

export type GetOptions = React.ComponentProps<typeof AsyncMultiSelect>['getOptions'];

export type AsyncMultiSelectImplementationProps = Omit<
    React.ComponentProps<typeof AsyncMultiSelect>,
    'getOptions'
>;

/**
 *  get the option details from 'options' list, for given newKey
 */
const getSelectOption = (
    newKey: string | number | boolean,
    optionLabel: string,
    options: SelectOptions
): OptionTypeBase | null => {
    let selectedOption = null;
    for (const option of options) {
        if ('options' in option && option.options.length) {
            for (const eachOption of option.options) {
                if (eachOption.value === newKey && eachOption.label === optionLabel) {
                    selectedOption = eachOption;
                    break;
                }
            }
        } else {
            if ((option as OptionTypeBase).value === newKey) {
                selectedOption = option as OptionTypeBase;
                break;
            }
        }
    }
    return selectedOption;
};

export const AsyncMultiSelect = React.forwardRef(function AsyncMultiSelect(
    props: Omit<SelectProps, 'onChange'> & {
        value?: OptionTypeBase[];
        minimumQueryLength?: number;
        onChange?: OnChangeHandler;
        getOptions: (query: string) => Promise<SelectOptions>;
        debounceTimer?: number;
    },
    ref
): React.ReactElement {
    // destruct all non-SelectProps from props. all props in 'restSelectProps' will be passed into arc Select component.
    // DO NOT include any props that arc Select cannot recognize in 'restSelectProps'
    const {
        value,
        emptyState,
        getOptions,
        debounceTimer,
        minimumQueryLength,
        ...restSelectProps
    } = props;
    const [inputHasValue, setInputHasValue] = useState<boolean>(false);

    const [state, dispatch] = React.useReducer(
        (state: State, action: Actions): State => {
            switch (action.type) {
                case 'SET_OPTIONS':
                    return {
                        loading: false,
                        error: undefined,
                        options: action.options,
                    };
                case 'SET_LOADING':
                    return {
                        loading: true,
                        error: undefined,
                        options: [],
                    };
                case 'SET_ERROR':
                    return {
                        loading: false,
                        error: action.error,
                        options: [],
                    };
                default:
                    return state;
            }
        },
        { options: [], loading: false, error: undefined }
    );

    const fetchOptions = React.useMemo(() => {
        return debounce(async (input: string) => {
            if (input.trim().length < (minimumQueryLength ?? 0)) {
                return;
            }
            try {
                dispatch({ type: 'SET_LOADING' });
                const options = await getOptions(input.trim());
                if (options) {
                    dispatch({
                        type: 'SET_OPTIONS',
                        options,
                    });
                } else {
                    dispatch({ type: 'SET_OPTIONS', options: [] });
                }
            } catch (e) {
                dispatch({
                    type: 'SET_ERROR',
                    error: 'Failed to retrieve options',
                });
            }
        }, debounceTimer ?? DEFAULT_DEBOUNCE_TIMER);
    }, [getOptions, debounceTimer, minimumQueryLength]);

    const { options } = state;
    const { onChange } = props;

    const onChangeMemo = React.useMemo(() => {
        return (
            e: Parameters<
                Exclude<React.ComponentProps<typeof BaseSelect>['onChange'], undefined>
            >[0]
        ) => {
            let selectedOption = null;
            const existingSelectedOptions = value ?? [];
            // value from target. for multi select. it's an array of all selected options (including the new one about to add). value/key only, label is not included.
            const targetValues = (e.target.value as (string | number | boolean)[]) ?? [];
            // selectedOptionsData from detail to get not only the option id, but also option label and other existing data
            const selectedOptionsData = e.detail.selectedOptionsData ?? [];
            // when targetValues has more elements than existingSelectedOptions, the 'onChange' is triggered by adding a new value to multi select
            const isAdding = targetValues.length > existingSelectedOptions.length;
            const isRemoving = targetValues.length < existingSelectedOptions.length;

            if (isAdding) {
                const existingSelectedOptionsMap = keyBy(existingSelectedOptions, 'value');
                const newKey = targetValues.find(
                    (key) => !existingSelectedOptionsMap[key as number]
                );
                const optionLabel = selectedOptionsData.find((option) => option.value === newKey)
                    ?.label;

                if (newKey && optionLabel) {
                    selectedOption = getSelectOption(newKey, optionLabel, options);

                    if (selectedOption) {
                        const updatedSelectedOptions = [
                            ...existingSelectedOptions,
                            ...[selectedOption],
                        ];
                        onChange?.(updatedSelectedOptions);
                    }
                }
            } else if (isRemoving) {
                // remove the one that is not in targetValues (triggered by click the x in multi select),
                // pass updatedSelectedOptions (including the new selected one) back via onChange?.(updatedSelectedOptions);
                const updatedSelectedOptions = existingSelectedOptions.filter((option) =>
                    targetValues.includes(option.value)
                );
                onChange?.(updatedSelectedOptions);
            }
        };
    }, [value, onChange, options]);

    const onInputChangeMemo = React.useMemo(() => {
        return (e: OnInputChangeParams[0], newValue: OnInputChangeParams[1]) => {
            setInputHasValue(!!newValue);
            fetchOptions(newValue);
        };
    }, [fetchOptions]);

    return (
        <BaseSelect
            filterOption={() => true}
            {...omitRef(restSelectProps)}
            // @ts-expect-error ref type mismatch but it works
            ref={ref}
            isLoading={state.loading}
            value={value}
            emptyState={inputHasValue ? 'No results found' : emptyState ?? 'Type to search'}
            options={state.options}
            isClearable={value !== null}
            isMultiple={true}
            matchSort={false}
            onInputChange={onInputChangeMemo}
            onChange={onChangeMemo}
        />
    );
});
